import { useState } from "react";
import { apiRequest, ApiError } from "../../lib/api.ts";
import { useAuth } from "../../lib/auth.tsx";
import { Button } from "../../components/ui/Button.tsx";
import { Dialog } from "../../components/ui/Dialog.tsx";

interface BulkResult {
  pushed: number;
  total: number;
  results: { mac: string; ok: boolean; reason?: string; version?: number }[];
}

interface Props {
  macs: string[];
  onClose: () => void;
  onPushed: () => void;
}

export function BulkConfigDialog({ macs, onClose, onPushed }: Props) {
  const { token } = useAuth();
  const [payloadStr, setPayloadStr] = useState("{}");
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BulkResult | null>(null);

  async function onPush() {
    let payload: Record<string, unknown>;
    try {
      const parsed = JSON.parse(payloadStr);
      if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
        setError("Payload must be a JSON object.");
        return;
      }
      payload = parsed as Record<string, unknown>;
    } catch (err) {
      setError("Invalid JSON: " + (err as Error).message);
      return;
    }

    setRunning(true);
    setError(null);
    try {
      const res = await apiRequest<BulkResult>("/admin/devices/bulk-config", {
        method: "POST",
        body: { macs, payload },
        token,
      });
      setResult(res);
      onPushed();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "push failed");
    } finally {
      setRunning(false);
    }
  }

  return (
    <Dialog title="Bulk push config" onClose={onClose}>
      <p className="mb-3 text-xs text-text2">
        Replaces <code className="font-mono text-text">config</code> on{" "}
        <span className="text-text">{macs.length}</span> selected device
        {macs.length === 1 ? "" : "s"} and bumps each{" "}
        <code className="font-mono text-text">config_version</code>.
      </p>

      <label className="block text-[10.5px] font-semibold uppercase tracking-[0.06em] text-text3">
        Config payload (JSON)
      </label>
      <textarea
        value={payloadStr}
        onChange={(e) => setPayloadStr(e.target.value)}
        rows={10}
        spellCheck={false}
        className="mt-1.5 w-full rounded-[9px] border border-line bg-bg px-3 py-2 font-mono text-xs text-text outline-none transition focus:border-brand1 focus:ring-2 focus:ring-brand1/25"
      />

      {error ? (
        <div className="mt-3 rounded-[9px] border border-g3/40 bg-g3/10 px-3 py-2 text-xs text-g3">
          {error}
        </div>
      ) : null}

      {result ? <BulkResultBox result={result} /> : null}

      <div className="mt-5 flex justify-end gap-2">
        <Button size="md" onClick={onClose}>
          {result ? "Close" : "Cancel"}
        </Button>
        {!result ? (
          <Button variant="primary" size="md" onClick={onPush} disabled={running}>
            {running ? "Pushing…" : `Push to ${macs.length}`}
          </Button>
        ) : null}
      </div>
    </Dialog>
  );
}

function BulkResultBox({ result }: { result: BulkResult }) {
  const failed = result.results.filter((r) => !r.ok);
  return (
    <div className="mt-3 max-h-60 overflow-y-auto rounded-[9px] border border-brand1/40 bg-brand1/[0.08] px-3 py-2 text-xs text-brand2">
      <div className="font-semibold">
        Pushed {result.pushed} of {result.total} device{result.total === 1 ? "" : "s"}.
      </div>
      {failed.length > 0 ? (
        <details className="mt-2">
          <summary className="cursor-pointer text-g2">
            {failed.length} failed — click for details
          </summary>
          <ul className="ml-4 mt-1 list-disc">
            {failed.map((r) => (
              <li key={r.mac} className="font-mono text-[11px]">
                {r.mac}
                {r.reason ? <span className="ml-2 text-text2">({r.reason})</span> : null}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}
